import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppDownloadCards } from "@/components/home/AppDownloadCards";
import { Search, Loader2, Sparkles, Award, Globe, ShieldCheck, Layers, Lock, Headset } from "lucide-react";
import { Header, SwissCross } from "@/components/Header";
import { SeoReport } from "@/components/SeoReport";
import { TargetingForm } from "@/components/TargetingForm";
import { CampaignDashboard } from "@/components/CampaignDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { analyzeUrl, analyzeQuota, createManagedCampaign, bundleConfirm } from "@/lib/api";
import { PaidScanDialog } from "@/components/home/PaidScanDialog";
import { ValueProposition } from "@/components/ValueProposition";
import { NeuralBackground } from "@/components/home/NeuralBackground";
import { SiteFooter } from "@/components/SiteFooter";
import { SecuritySection, AutodeletingBadge } from "@/components/SecuritySection";
import { ServiceKeywords } from "@/components/home/ServiceKeywords";
import { CertificationsSection } from "@/components/home/CertificationsSection";
import { CertMarquee } from "@/components/home/CertMarquee";
import { HeroTitle } from "@/components/home/HeroTitle";
import { SecurityLeadersMarquee } from "@/components/home/SecurityLeadersMarquee";
import { PromoBanner } from "@/components/analysis/PromoBanner";
import { PackageCards } from "@/components/home/PackageCards";
import { HomeServices } from "@/components/home/HomeServices";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { TID } from "@/constants/testIds";

const HOME_SEO = {
  title: "AURORA-ADV | Marketing Swiss Made, SEO e ADV in Ticino",
  description: "Piattaforma marketing digitale in Ticino. SEO locale, campagne ADV, social e web/app con tecnologia Aurora®. Sicurezza svizzera e report sempre trasparenti.",
  path: "/",
};

export default function Home() {
  const { lang, setLang, t, tr } = useLang();
  const { user } = useAuth();
  useSeo(HOME_SEO);

  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [quota, setQuota] = useState(null);
  const [payInfo, setPayInfo] = useState(null);
  const loadQuota = () => analyzeQuota().then(setQuota).catch(() => {});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid_analysis");
    const bundle = params.get("bundle_paid");
    if (bundle) {
      window.history.replaceState(null, "", window.location.pathname);
      bundleConfirm(bundle)
        .then((d) => toast.success(tr(`${d.added} analisi aggiunte al tuo account: ne hai ${d.credits} disponibili`, `${d.added} analyses added to your account: ${d.credits} available`)))
        .catch((e) => toast.error(e?.response?.data?.detail || tr("Pagamento non confermato", "Payment not confirmed")))
        .finally(loadQuota);
    } else if (paid) {
      window.history.replaceState(null, "", window.location.pathname);
      onAnalyze(paid);
    } else loadQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAnalyze = async (paidId) => {
    const paid = typeof paidId === "string" ? paidId : null;
    if (!paid && (!url || url.length < 4)) {
      toast.error(t("invalidUrl"));
      return;
    }
    setAnalyzing(true);
    setAnalysis(null);
    setCampaignId(null);
    try {
      const data = await analyzeUrl(url, lang, paid);
      if (paid) setUrl(data.analysis?.url || "");
      setAnalysis(data);
      // scroll to report
      setTimeout(() => {
        document.getElementById("report")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "payment_required") setPayInfo(d);
      else toast.error((typeof d === "string" && d) || tr("Errore analisi", "Analysis error"));
    } finally {
      setAnalyzing(false);
      loadQuota();
    }
  };

  const onLaunch = async (payload) => {
    setLaunching(true);
    try {
      const camp = await createManagedCampaign({
        analysis_id: analysis.id,
        ...payload,
        origin_url: window.location.origin,
      });
      if (camp.checkout_url) {
        toast.info(tr("Redirect a Stripe per pagamento sicuro...", "Redirecting to Stripe for secure payment..."));
        window.location.href = camp.checkout_url;
        return;
      }
      setCampaignId(camp.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Errore lancio campagna", "Campaign launch error"));
    } finally {
      setLaunching(false);
    }
  };

  const reset = () => {
    setAnalysis(null);
    setCampaignId(null);
    setUrl("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#E6EAEC]">
      <Header lang={lang} setLang={setLang} t={t} />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#cfd6d9]">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="matrix-overlay" />
        <NeuralBackground />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-3">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <div className="inline-flex items-center gap-2 border border-[#e4e4e7] px-3 py-1 bg-white">
                  <Sparkles className="w-3.5 h-3.5 text-[#E0262C]" />
                  <span className="text-[10px] uppercase tracking-widest font-mono text-[#71717a]">
                    {tr("ADV · SOCIAL · DATI REALI", "ADV · SOCIAL · REAL DATA")}
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 border border-[#e4e4e7] px-3 py-1 bg-white">
                  <SwissCross className="w-3 h-3" />
                  <span className="text-[10px] uppercase tracking-widest font-mono text-[#71717a]">
                    Made in Switzerland
                  </span>
                </div>
                <AutodeletingBadge />
                <div className="inline-flex items-center gap-2 border border-[#e4e4e7] px-3 py-1 bg-[#09090b] text-white">
                  <Award className="w-3 h-3 text-[#FF8A3D]" />
                  <span className="text-[10px] uppercase tracking-widest font-mono">
                    {tr("Unica e irripetibile", "One of a kind")}
                  </span>
                </div>
              </div>
              <h1 data-testid="home-h1" className="text-[11px] sm:text-xs uppercase tracking-[0.2em] font-mono text-[#2B3036] mb-4">{tr("AURORA-ADV: marketing digitale Swiss Made per PMI in Ticino", "AURORA-ADV: Swiss Made digital marketing for SMEs in Ticino")}</h1>
              <HeroTitle a={t("heroTitleA")} b={t("heroTitleB")} />
              <p data-testid="hero-subtitle" className="text-base lg:text-lg text-[#52525b] max-w-2xl leading-relaxed mb-10">
                <strong className="block text-[#2B3036] font-semibold mb-1">{t("heroSubtitleLead")}</strong>
                {t("heroSubtitle")}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717a]" />
                  <Input
                    data-testid={TID.urlInput}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !analyzing && onAnalyze()}
                    placeholder={t("urlPlaceholder")}
                    className="pl-11 h-14 rounded-sm border-[#e4e4e7] bg-white font-mono text-sm"
                    disabled={analyzing}
                  />
                </div>
                <Button
                  data-testid={TID.analyzeBtn}
                  onClick={onAnalyze}
                  disabled={analyzing}
                  className="bg-[#09090b] hover:bg-[#E0262C] text-white h-14 px-8 rounded-sm font-medium transition-colors"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("analyzing")}
                    </>
                  ) : (
                    <>
                      {t("analyzeBtn")}
                      {quota && !quota.unlimited && (
                        <span data-testid="analyze-price-badge" className={`ml-2 text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 ${quota.free_available ? "bg-[#10b981] text-white" : "bg-white text-[#09090b]"}`}>
                          {quota.free_available ? tr("Gratis", "Free") : quota.credits > 0 ? tr(`${quota.credits} prepagate`, `${quota.credits} prepaid`) : "CHF 5"}
                        </span>
                      )}
                    </>
                  )}
                </Button>
              </div>
              <p data-testid="analyze-quota-hint" className="mt-2 text-[11px] font-mono text-[#71717a]">{tr("1 analisi gratuita al mese, le successive CHF 5 oppure 5 analisi a CHF 15", "1 free analysis per month, then CHF 5 each or 5 analyses for CHF 15")}</p>
              <PaidScanDialog info={payInfo} quota={quota} url={url} lang={lang} onClose={() => setPayInfo(null)} />
              {!analysis && <ServiceKeywords onPick={() => document.querySelector('[data-testid="value-proposition"]')?.scrollIntoView({ behavior: "smooth" })} />}

              {analyzing && (
                <div className="mt-6 font-mono text-xs text-[#71717a] space-y-1">
                  <div>▸ {t("fetching")}</div>
                  <div>▸ {t("parsing")}</div>
                  <div>▸ {t("scoring")}</div>
                  <div className="aurora-text font-semibold">▸ {t("aiRunning")}</div>
                </div>
              )}

              {/* Social proof row */}
              <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-[#71717a]">
                <div className="flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5" />
                  <span className="font-mono">{tr("ADV + Instagram + TikTok + contatti", "ADV + Instagram + TikTok + contacts")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#10b981]" />
                  <span className="font-mono">{tr("Dati reali verificati", "Verified real data")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-3.5 h-3.5 text-[#FF8A3D]" />
                  <span className="font-mono">{tr("Tutto in una soluzione", "All in one solution")}</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 hidden lg:block">
              <div className="border border-[#e4e4e7] bg-white p-6 space-y-6 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-20 blur-3xl aurora-gradient-radial" />
                <div className="relative">
                  <MiniStat label={tr("Budget e servizio flessibili", "Flexible budget and service")} value={tr("A pacchetti: sfrutta solo quello che ti serve", "Packages: use only what you need")} />
                </div>
                <div className="relative">
                  <MiniStat label={tr("Canali in un'unica soluzione", "Channels in one solution")} value="ADV · IG · TikTok" />
                </div>
                <div className="relative">
                  <MiniStat label={tr("Scalabile", "Scalable")} value={tr("Mese per mese", "Month by month")} aurora />
                </div>
                <div className="pt-4 border-t border-[#e4e4e7] flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#2B3036] text-white inline-flex items-center justify-center shrink-0"><Headset className="w-4 h-4" /></span>
                  <span data-testid="hero-service-line" className="text-xs text-[#71717a] font-mono">{tr("Tecnici in Ticino, risposta entro 24 ore", "Technicians in Ticino, reply within 24 hours")}</span>
                </div>
                <div className="pt-3 border-t border-[#e4e4e7] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SwissCross className="w-3.5 h-3.5" />
                    <span data-testid="hero-hq" className="text-[10px] font-mono uppercase tracking-widest text-[#71717a]">
                      {tr("Lugano · Sede", "Lugano · HQ")}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-[#a1a1aa] inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Swiss Made</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {!analysis && <ValueProposition />}
      {!analysis && <HomeServices />}
      {!analysis && <PackageCards />}
      {!analysis && <SecuritySection />}
      {!analysis && <CertMarquee />}
      {!analysis && <CertificationsSection />}
      {!analysis && <SecurityLeadersMarquee />}

      {/* Report */}
      {analysis && (
        <section id="report" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SeoReport data={analysis} t={t} />
          <div className="mt-10"><PromoBanner data={analysis} /></div>
          <div className="-mx-6 lg:-mx-10"><PackageCards /></div>
        </section>
      )}

      {/* Targeting */}
      {analysis && !campaignId && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
          {!user ? (
            <div className="border border-[#09090b] bg-[#09090b] text-white p-10 text-center">
              <h3 className="font-display text-2xl mb-3">{tr("Accedi per lanciare una campagna", "Log in to launch a campaign")}</h3>
              <p className="text-sm text-[#a1a1aa] mb-6 max-w-md mx-auto">
                {tr("Serve un account per pagare la campagna e seguire giorno per giorno contatti, profili social e statistiche.", "You need an account to pay for the campaign and follow contacts, social profiles and statistics day by day.")}
              </p>
              <div className="flex gap-3 justify-center">
                <a href="/login" className="bg-white text-black px-6 py-2.5 text-sm font-medium hover:aurora-gradient hover:text-white transition">{tr("Accedi", "Log in")}</a>
                <a href="/register" className="aurora-gradient text-white px-6 py-2.5 text-sm font-medium hover:opacity-90">{tr("Registrati", "Sign up")}</a>
              </div>
            </div>
          ) : (
            <TargetingForm onLaunch={onLaunch} loading={launching} analysisId={analysis.id} />
          )}
        </section>
      )}

      {/* Dashboard */}
      {campaignId && (
        <section id="dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <CampaignDashboard campaignId={campaignId} onReset={reset} />
        </section>
      )}

      <AppDownloadCards />

      <SiteFooter />
    </div>
  );
}

const MiniStat = ({ label, value, aurora }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest font-mono text-[#71717a] mb-1">{label}</div>
    <div className={`font-display text-3xl leading-none ${aurora ? "aurora-text" : ""}`}>{value}</div>
  </div>
);
